"use client"

import { Fragment, useState } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { IconCalendarCheck, IconCalendarPlus, IconChevronRight, IconCircleCheck, IconPlus } from "@tabler/icons-react"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useAppointmentSheet } from "@/components/appointments/sheet/appointment-sheet-context"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import api from "@/lib/api"
import { cn } from "@/lib/utils"
import { formatCurrencyStatic } from "@/hooks/use-currency"

import { CareTeamSlot } from "@/components/leads/care-team-slot"
import { FlightCheckInToggle } from "@/components/dashboard/variants/frontdesk/flight-check-in-toggle"
import { RideDriverAction } from "@/components/dashboard/variants/frontdesk/ride-driver-action"
import { LeadTreatmentPlan } from "@/components/leads/lead-treatment-plan"
import { TravelFormDialog } from "@/components/leads/travel-form-dialog"
import { useAuthStore } from "@/stores/auth-store"
import { calendarPreset } from "@/lib/appointment-presets"

import {
  ARRIVAL_VISIT_SWATCH,
  LATE_ARRIVAL_FROM,
  REASON_ICON,
  REASON_LABEL_KEY,
  toneFor,
  URGENCY_RAIL,
  URGENCY_TEXT,
  visitBucketFor,
  visitLabelKeyFor,
  type useNeedsBooking,
} from "@/components/dashboard/variants/frontdesk/use-needs-booking"

/**
 * Front-desk "Needs booking" board — the table half.
 *
 * ONE list for the desk's whole queue, because the desk does not think in
 * sources — it thinks "who do I have to call, and by when". Arrivals,
 * departures, lab work coming back, invoices and the doctor's booking requests
 * all land here, ordered by the day the action is needed, most urgent first.
 *
 * Purely presentational: every row, count and filter comes from
 * {@link useNeedsBooking}, which the PAGE owns — the filters that drive it live
 * in the page header, and two sibling surfaces cannot each hold the state.
 *
 * Rows deep-link to the PATIENT, never to /lab-orders/{id} or /invoices/{id}:
 * front desk holds leads.view.all, so the patient page is the one target that
 * works for every reason here.
 */
export function NeedsBookingTable({
  board,
  className,
  gutter = true,
}: {
  board: ReturnType<typeof useNeedsBooking>
  className?: string
  /**
   * Whether the board supplies its own outer padding. Default true.
   *
   * On the front-desk page this board IS the screen — it mounts straight into
   * the page shell, so the padding here is the page gutter and there is nothing
   * else for it to line up with. On the coordinator dashboard it is one panel
   * among others in a column that already has `p-3 md:p-4`, and keeping this
   * padding put the board's border 12px inside every sibling panel's — visibly
   * misaligned down the whole left edge (measured and reported 260907). Pass
   * false wherever a parent already owns the gutter.
   */
  gutter?: boolean
}) {
  const t = useTranslations("dashboard")
  // Dentour 261015: arrival planes in the calendar's visit colours (swatch
  // badge: white / green / brown) instead of a tinted stroke.
  const visitSwatches = useAuthStore((s) =>
    calendarPreset(s.user?.appointment_presets, "travel_visit_colors"),
  )
  const formatter = useFormatter()
  const router = useRouter()
  const {
    allowed,
    isLoading,
    visible,
    shortDate,
    showCareTeam,
    showDrivers,
    bookButton,
    checkInMode,
    rows,
    refresh,
    appointmentsChanged,
  } = board
  // The row's own Book action: this board exists to say "who needs an
  // appointment", so booking happens right here — the appointment sheet opens
  // with the patient preselected instead of bouncing through the lead page.
  const appointmentSheet = useAppointmentSheet()
  // Radiance 261005 — the patient whose first trip the desk is adding from a
  // "No trip yet" row; the trip form opens right here, not on the patient page.
  const [addingTrip, setAddingTrip] = useState<{ leadId: string; name: string } | null>(null)
  const canCreateTrip = useAuthStore((s) => !!s.user?.permissions?.includes("travels.create"))
  // Dentour 260923 — rows opened to show the patient's Agreed plan.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  // ArtDent 261015 — a doctor's follow-up request the desk answered without a
  // booking (by phone, or the patient declined): off the board, no visit. The
  // board reloads either way, so a failed dismiss restores the row.
  const dismissFollowup = async (followupId: string) => {
    try {
      await api.post(`/booking-worklist/followups/${followupId}/dismiss`)
    } finally {
      refresh()
    }
  }

  if (!allowed) return null
  const columnCount = 5 + (showDrivers ? 1 : 0) + (showCareTeam ? 1 : 0)

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  return (
      <div className={cn("min-h-0 flex-1 overflow-hidden", gutter && "p-2 md:p-3")}>
        <ApolloPanel
          eyebrow={t(checkInMode ? "frontdeskBooking.titleLogistics" : "frontdeskBooking.title")}
          // The board's total, in the table's own top-right corner rather than
          // inside a filter label — it describes what is on screen, so it
          // belongs to the table, and it stays visible whichever reason is
          // selected.
          actions={
            isLoading ? null : (
              <Badge
                variant="outline"
                className="border-sky-500/40 text-[10px] text-sky-600 dark:text-sky-500"
              >
                {t("frontdeskBooking.count", { count: visible.length })}
              </Badge>
            )
          }
          className={cn("flex h-full min-h-0 flex-col", className)}
          // Tight only when this board is the whole screen. As one panel
          // among others the header keeps ApolloPanel's own padding, so
          // "NEEDS BOOKING" starts on the same x as every sibling panel's
          // eyebrow — the tight override would leave it 10px to their left.
          headerClassName={gutter ? "px-2.5 pt-2.5 md:px-2.5" : undefined}
          bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden p-0 pt-1.5"
        >
          <div className="scrollbar-thin min-h-0 flex-1 overflow-auto">
            {isLoading ? (
              <div className="space-y-2 p-2.5">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-3.5 w-1/5" />
                    <Skeleton className="h-4 w-16 shrink-0 rounded-full" />
                    <Skeleton className="h-3.5 flex-1" />
                    <Skeleton className="h-3.5 w-14 shrink-0" />
                  </div>
                ))}
              </div>
            ) : visible.length === 0 ? (
              <div className="flex h-full min-h-[180px] flex-col items-center justify-center text-center">
                <div className="mb-2 rounded-full bg-emerald-500/10 p-2.5">
                  <IconCalendarPlus className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <p className="text-xs font-medium text-muted-foreground">
                  {!checkInMode
                    ? t("frontdeskBooking.empty.title")
                    : rows.length === 0
                      ? t("frontdeskBooking.emptyLogistics.none")
                      : t("frontdeskBooking.emptyLogistics.filtered")}
                </p>
                <p className="mt-0.5 text-[11px] text-muted-foreground/70">
                  {t(checkInMode ? "frontdeskBooking.emptyLogistics.body" : "frontdeskBooking.empty.body")}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="h-7 py-1 text-[10px]">
                      {t("frontdeskBooking.columns.patient")}
                    </TableHead>
                    <TableHead className="h-7 py-1 text-[10px]">
                      {t("frontdeskBooking.columns.why")}
                    </TableHead>
                    <TableHead className="h-7 py-1 text-[10px]">
                      {t("frontdeskBooking.columns.detail")}
                    </TableHead>
                    {/* Dentour 260923 — who collects / drops the patient at
                        the airport, dispatched right here. Before the care
                        team: for a landing patient the ride comes first. */}
                    {showDrivers && (
                      <TableHead className="h-7 whitespace-nowrap py-1 text-[10px]">
                        {t("frontdeskBooking.columns.driver")}
                      </TableHead>
                    )}
                    {/* Phase 82 — who this patient belongs to, right where
                        the desk decides who to book them with. The column is
                        driven by the payload: a clinic that has not opted in
                        is served no care_team block and never sees it. */}
                    {showCareTeam && (
                      <TableHead className="h-7 py-1 text-[10px]">
                        {t("frontdeskBooking.columns.careTeam")}
                      </TableHead>
                    )}
                    <TableHead className="h-7 py-1 text-right text-[10px]">
                      {t("frontdeskBooking.columns.when")}
                    </TableHead>
                    {checkInMode ? (
                      <TableHead className="h-7 whitespace-nowrap py-1 text-right text-[10px]">
                        {t("frontdeskBooking.checkIn.column")}
                      </TableHead>
                    ) : (
                      <TableHead className="h-7 w-9 whitespace-nowrap py-1" />
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visible.map((row) => (
                    <Fragment key={row.id}>
                    <TableRow
                      role="button"
                      tabIndex={0}
                      onClick={() => router.push(`/leads/${row.leadId}`)}
                      onKeyDown={(e) => {
                        // Only when the ROW itself has focus. Keys typed inside
                        // a cell's controls bubble up here too — including from
                        // their portalled popovers, which React routes through
                        // the component tree — so without this, Enter on the
                        // care-team button or a space in its doctor search
                        // opened the patient page instead.
                        if (e.target !== e.currentTarget) return
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault()
                          router.push(`/leads/${row.leadId}`)
                        }
                      }}
                      className="cursor-pointer"
                    >
                      <TableCell className="py-1.5">
                        <span className="flex items-center gap-2">
                          {/* A booked row is settled: its rail goes quiet so the
                              colour never implies work that is already handled. */}
                          <span
                            className={cn(
                              "h-5 w-[3px] shrink-0 rounded-full",
                              row.booked ? "bg-emerald-500/40" : URGENCY_RAIL[row.urgency],
                            )}
                            aria-hidden
                          />
                          {/* The Agreed-plan expander — only when the patient has one. */}
                          {row.hasAgreedPlan ? (
                            <button
                              type="button"
                              className="-ml-1 grid size-5 shrink-0 place-items-center rounded text-text-dim hover:bg-surface-2 hover:text-foreground"
                              aria-expanded={expanded.has(row.id)}
                              aria-label={t("frontdeskBooking.plan.toggle", { name: row.patientName })}
                              title={t("frontdeskBooking.plan.toggle", { name: row.patientName })}
                              onClick={(e) => {
                                e.stopPropagation()
                                toggle(row.id)
                              }}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <IconChevronRight
                                className={cn("size-3.5 transition-transform", expanded.has(row.id) && "rotate-90")}
                              />
                            </button>
                          ) : (
                            <span className="-ml-1 size-5 shrink-0" aria-hidden />
                          )}
                          {/* A real link, so Ctrl/Cmd-click and middle-click open
                              the patient in a new tab and the board stays put.
                              Stopped here so the row's own click does not
                              navigate a second time. */}
                          <Link
                            href={`/leads/${row.leadId}`}
                            className="truncate text-[13px] font-medium underline-offset-2 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            {row.patientName}
                          </Link>
                          {/* Radiance 261005 — the Kapar not yet on an invoice. */}
                          {row.deposits.length > 0 && (
                            <span
                              className="shrink-0 whitespace-nowrap rounded-full border border-success/30 bg-success-tint px-1.5 text-[10px] leading-4 text-success"
                              title={t("frontdeskBooking.deposit.title")}
                            >
                              {t("frontdeskBooking.deposit.label", {
                                amount: row.deposits
                                  .map((d) => formatCurrencyStatic(d.amount, d.currency, { decimals: false }))
                                  .join(" + "),
                              })}
                            </span>
                          )}
                          {row.booked && (
                            <IconCalendarCheck
                              className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400"
                              aria-label={t(checkInMode ? "frontdeskBooking.booking.doneAria" : "frontdeskBooking.booking.bookedAria")}
                            />
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="py-1.5">
                        {(() => {
                          const Icon = REASON_ICON[row.reason]
                          // Arrival planes are coloured by which visit this is
                          // for the patient, so the colour carries meaning the
                          // reason label does not — it gets a real label rather
                          // than aria-hidden. Every other icon stays decorative.
                          const visitKey = visitLabelKeyFor(row)
                          const swatch = visitSwatches ? visitBucketFor(row) : null
                          return (
                            <span className="flex items-center gap-1.5">
                              {swatch !== null && visitKey ? (
                                <span
                                  role="img"
                                  aria-label={t(visitKey)}
                                  title={t(visitKey)}
                                  className={cn(
                                    "flex size-4 shrink-0 items-center justify-center rounded-sm",
                                    ARRIVAL_VISIT_SWATCH[swatch],
                                  )}
                                >
                                  <Icon aria-hidden className="size-3" />
                                </span>
                              ) : (
                                <Icon
                                  className={cn("h-4 w-4 shrink-0", toneFor(row))}
                                  {...(visitKey
                                    ? { "aria-label": t(visitKey), title: t(visitKey) }
                                    : { "aria-hidden": true })}
                                />
                              )}
                              <span className="truncate text-[11px] text-text-dim">
                                {t(REASON_LABEL_KEY[row.reason])}
                              </span>
                            </span>
                          )
                        })()}
                      </TableCell>
                      {/* Takes the width that is left and truncates, so the
                          action column (a drive button or chip on departures)
                          always fits instead of pushing the table wider. */}
                      <TableCell className="w-full max-w-0 py-1.5 text-[11px] text-text-dim">
                        <span className="flex min-w-0 items-center gap-1.5">
                          {/* Radiance 261005 — lands too late for a visit that
                              day: the logistics desk leaves the first
                              appointment for tomorrow. Ahead of the text, which
                              truncates, so the note never gets cut off. */}
                          {checkInMode && row.visitNextDay && (
                            <span
                              className="shrink-0 whitespace-nowrap rounded-full border border-sky-500/40 px-1.5 text-[10px] leading-4 text-sky-700 dark:text-sky-400"
                              title={t("frontdeskBooking.lateArrival.title", { time: LATE_ARRIVAL_FROM })}
                            >
                              {t("frontdeskBooking.lateArrival.label")}
                            </span>
                          )}
                          <span className="block min-w-0 truncate" title={row.detail}>{row.detail}</span>
                        </span>
                      </TableCell>
                      {showDrivers && (
                        <TableCell className="whitespace-nowrap py-1.5">
                          {/* No airport ride for this reason: a dash, never a blank cell. */}
                          {row.ride ? (
                            <RideDriverAction row={row} onChanged={refresh} />
                          ) : (
                            <span className="text-[11px] text-text-dim">—</span>
                          )}
                        </TableCell>
                      )}
                      {showCareTeam && (
                        <TableCell className="py-1.5">
                          {/* Both slots, each its own control (Q-5): an Assign
                              affordance for care_team.assign holders, plain
                              text for everyone else — CareTeamSlot decides.
                              refresh() re-pulls the board so a just-assigned
                              name replaces the Assign button in place. */}
                          <span className="flex flex-col items-start gap-0.5">
                            <CareTeamSlot
                              leadId={row.leadId}
                              slot="doctor"
                              currentId={row.careTeam?.doctorId ?? null}
                              currentName={row.careTeam?.doctorName ?? null}
                              onChanged={refresh}
                              dense
                            />
                            <CareTeamSlot
                              leadId={row.leadId}
                              slot="prosthetist"
                              currentId={row.careTeam?.prosthetistId ?? null}
                              currentName={row.careTeam?.prosthetistName ?? null}
                              onChanged={refresh}
                              dense
                              className="text-text-dim"
                            />
                          </span>
                        </TableCell>
                      )}
                      <TableCell
                        className={cn(
                          "whitespace-nowrap py-1.5 text-right text-[10px] tabular-nums",
                          row.booked ? "text-text-dim" : URGENCY_TEXT[row.urgency],
                        )}
                      >
                        {row.date === null
                          ? "—"
                          : row.diffDays === 0
                            ? t("frontdeskBooking.urgency.today")
                            : formatter.relativeTime(row.date, today)}
                        {row.date && (
                          <span className="ml-1 text-muted-foreground/70">
                            {shortDate(row.date)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap py-1 pr-1.5 text-right">
                        {/* Radiance 261001 — the logistics desk books no visits:
                            its rows (arrivals and departures only) carry the
                            patient's flight check-in here instead. */}
                        {checkInMode && row.checkIn && (
                          <span className="inline-flex items-center gap-1.5">
                            {/* The check-in needs the passport: say so right
                                beside the Yes / No it would block. */}
                            {row.identityOnFile === false && (
                              <span
                                className="rounded-full border border-amber-500/40 px-1.5 text-[10px] leading-4 text-amber-700 dark:text-amber-400"
                                title={t("frontdeskBooking.checkIn.noIdTitle")}
                              >
                                {t("frontdeskBooking.checkIn.noId")}
                              </span>
                            )}
                            <FlightCheckInToggle
                              key={`${row.id}:${row.checkIn.checkedIn}`}
                              row={row}
                              onChanged={refresh}
                            />
                          </span>
                        )}
                        {/* Radiance 261005 — no flight to check in yet: the
                            desk records it from here. */}
                        {checkInMode && row.reason === "no_trip" && canCreateTrip && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 gap-1 px-2.5 text-[12px]"
                            onClick={(e) => {
                              e.stopPropagation()
                              setAddingTrip({ leadId: row.leadId, name: row.patientName })
                            }}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            <IconPlus className="size-3.5" aria-hidden />
                            {t("frontdeskBooking.noTrip.addTrip")}
                          </Button>
                        )}
                        {/* ArtDent 261015 — a doctor's request answered without
                            a booking: handled by phone, or the patient declined. */}
                        {!checkInMode && row.followupId && !row.booked && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="mr-1 size-7"
                            title={t("frontdeskBooking.booking.dismiss")}
                            aria-label={t("frontdeskBooking.booking.dismissAria", {
                              name: row.patientName,
                            })}
                            onClick={(e) => {
                              e.stopPropagation()
                              void dismissFollowup(String(row.followupId))
                            }}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            <IconCircleCheck className="size-4" aria-hidden />
                          </Button>
                        )}
                        {/* Book the VISIT. A departure with a ride has none to
                            book (its job is the drive, in the Driver column);
                            one without (drives hidden) books the visit before
                            the flight. A desk arrival shows it until its visit
                            half is done, even while it still waits for a driver.
                            The logistics desk (checkInMode) books no visits. */}
                        {!checkInMode &&
                          !(row.reason === "departing" && row.ride) &&
                          !(row.visitBooked ?? row.booked) && (
                          <Button
                            type="button"
                            // Dentour 261008: a labelled button — the icon alone
                            // was too easy to miss. Outline, like this table's
                            // other row actions: a solid brand fill on every row
                            // shouted over the rest of the board.
                            {...(bookButton
                              ? { variant: "outline" as const, size: "sm" as const, className: "h-7 gap-1.5 px-3 text-[12px]" }
                              : { variant: "ghost" as const, size: "icon" as const, className: "size-7" })}
                            aria-label={t("frontdeskBooking.booking.bookAria", {
                              name: row.patientName,
                            })}
                            onClick={(e) => {
                              e.stopPropagation()
                              appointmentSheet.createAppointment(
                                // A step row books THAT step: the link is what
                                // clears the row (and marks the step booked on
                                // the treatment itself).
                                row.stageId
                                  ? {
                                      leadId: row.leadId,
                                      stageIds: [row.stageId],
                                      // Phase 89 — prefill the step's doctor (warn, never lock).
                                      stageDoctorId: row.stageDoctorId ?? null,
                                    }
                                  : { leadId: row.leadId },
                                { onChanged: appointmentsChanged }
                              )
                            }}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            <IconCalendarPlus className="size-4" aria-hidden />
                            {bookButton && t("frontdeskBooking.booking.book")}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    {expanded.has(row.id) && (
                      <TableRow className="hover:bg-transparent">
                        {/* The patient page's own Agreed plan card, reused as
                            is: the accepted quote's procedures by arch, with
                            quantities and no prices. Loaded when opened. */}
                        <TableCell colSpan={columnCount} className="bg-surface/30 p-0">
                          <div className="max-w-3xl">
                            <LeadTreatmentPlan leadId={row.leadId} />
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </ApolloPanel>
        {addingTrip && (
          <TravelFormDialog
            open
            onOpenChange={(open) => !open && setAddingTrip(null)}
            leadId={addingTrip.leadId}
            leadName={addingTrip.name}
            onSuccess={() => {
              setAddingTrip(null)
              refresh()
            }}
          />
        )}
      </div>
  )
}
